import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users2, ChevronRight, Plus, X, Save, ShieldCheck, Building2, Activity, ClipboardList, BookOpen, Gauge, Brain, Sparkles, CheckCircle2, XCircle, AlertTriangle, FileText, TrendingUp, Clock, Award, HeartHandshake, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import StaffWorkspace from "@/components/admin/staff-workspace/StaffWorkspace";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const AUTHORITY_LEVELS = ["draft_only", "advisory", "execute_with_approval", "autonomous"];
const STAFF_STATUSES = ["defined", "training", "active", "paused", "retired"];

const AUTHORITY_LABEL = {
  draft_only: "Draft only",
  advisory: "Advisory",
  execute_with_approval: "Execute w/ approval",
  autonomous: "Autonomous",
};

const STATUS_TONE = {
  defined: "bg-bone/60 text-ink border-line",
  training: "bg-amber-100/60 text-amber-900 border-amber-200",
  active: "bg-forest text-bone border-forest",
  paused: "bg-bone/60 text-ink-soft border-line",
  retired: "bg-line text-ink-soft border-line",
};

export default function Staff() {
  const [departments, setDepartments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [overview, setOverview] = useState(null);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [filterDept, setFilterDept] = useState("");

  const load = useCallback(async () => {
    try {
      const [d, s, o] = await Promise.all([
        api.get("/staff/departments"),
        api.get("/staff", { params: filterDept ? { department: filterDept } : {} }),
        api.get("/staff/summary/overview"),
      ]);
      setDepartments(d.data);
      setStaff(s.data);
      setOverview(o.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [filterDept]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/staff/${id}`);
      setSelected(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="staff-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline flex items-center gap-2"><Users2 className="h-3.5 w-3.5 text-terracotta" strokeWidth={1.5} />Executive · AI Staff</div>
          <h1 className="font-serif text-4xl mt-2">Digital Workforce</h1>
          <p className="text-ink-soft mt-2 max-w-2xl text-sm leading-relaxed">
            Institutional Human Resources for a digital workforce. Every AI role is defined here — mission, authority, inputs, outputs, approvals — before execution is ever granted. Framework only in this sprint.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="staff-add-btn" className="bg-forest text-bone hover:bg-ink rounded-full">
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> Add Staff Member
        </Button>
      </header>

      {/* Performance Dashboard — high-level org state */}
      {overview && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="staff-overview">
          <Tile label="Total staff" value={overview.total_active_staff} icon={Users2} />
          <Tile label="Active" value={overview.by_status.active || 0} icon={Activity} tone="terracotta" />
          <Tile label="In definition" value={overview.by_status.defined || 0} icon={ClipboardList} />
          <Tile label="Activity · 30d" value={overview.activity_last_30d || 0} icon={Gauge} />
        </section>
      )}

      {/* Departments strip */}
      <section className="bg-admin-surface border border-line rounded-xl overflow-hidden" data-testid="staff-departments">
        <div className="px-5 py-3 border-b border-line bg-bone/40 flex items-center justify-between">
          <div className="overline flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-terracotta" strokeWidth={1.5} />Departments</div>
          {filterDept && (
            <button onClick={() => setFilterDept("")} className="text-xs text-ink-soft hover:text-terracotta" data-testid="staff-clear-filter">Clear filter ×</button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 divide-x divide-y md:divide-y-0 divide-line">
          {departments.map(d => (
            <button
              key={d.key}
              onClick={() => setFilterDept(filterDept === d.key ? "" : d.key)}
              data-testid={`staff-dept-${d.key}`}
              className={`p-4 text-left transition-colors duration-200 ${filterDept === d.key ? "bg-forest text-bone" : "hover:bg-bone/50"}`}
            >
              <div className={`text-[10px] uppercase tracking-widest ${filterDept === d.key ? "text-bone/70" : "text-ink-soft"}`}>{d.label}</div>
              <div className="font-serif text-2xl mt-1">{d.staff_count}</div>
            </button>
          ))}
        </div>
      </section>

      {/* Staff Directory */}
      <section className="bg-admin-surface border border-line rounded-xl overflow-hidden" data-testid="staff-directory">
        <div className="px-5 py-3 border-b border-line bg-bone/40 flex items-center justify-between">
          <div className="overline">Staff Directory {filterDept && `· ${filterDept}`}</div>
          <div className="text-xs text-ink-soft">{staff.length} record{staff.length === 1 ? "" : "s"}</div>
        </div>
        {staff.length === 0 ? (
          <div className="p-10 text-center text-ink-soft text-sm">
            No staff in this view yet. Every future AI role gets a record here.
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {staff.map(s => (
              <li key={s.id} className="flex items-center hover:bg-bone/40 transition-colors duration-200" data-testid={`staff-row-${s.id}`}>
                <button
                  onClick={() => openDetail(s.id)}
                  data-testid={`staff-row-open-${s.id}`}
                  className="flex-1 px-5 py-4 flex items-center gap-4 text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-forest text-bone flex items-center justify-center font-serif text-lg">
                    {s.name?.slice(0, 1)?.toUpperCase() || "·"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg text-ink">{s.name}</div>
                    <div className="text-xs text-ink-soft truncate">{s.position} · {(s.department || "").replace(/_/g, " ")}</div>
                  </div>
                  <Badge className={`text-[10px] uppercase tracking-widest border ${STATUS_TONE[s.status] || STATUS_TONE.defined}`}>{s.status}</Badge>
                  <Badge className="bg-bone/60 text-ink border-line text-[10px]">{AUTHORITY_LABEL[s.authority_level] || s.authority_level}</Badge>
                </button>
                <Link
                  to={`/admin/staff/${s.id}/workspace`}
                  data-testid={`staff-row-workspace-${s.id}`}
                  className="mr-4 shrink-0 h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 bg-forest text-bone hover:bg-ink transition-colors duration-150"
                  title="Open the Founder-facing Workspace"
                >
                  <Sparkles className="h-3 w-3" strokeWidth={1.5} /> Open Workspace
                </Link>
                <ChevronRight className="h-4 w-4 text-ink-soft mr-5 shrink-0" strokeWidth={1.5} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Staff Handbook footer — always visible so principle isn't hidden */}
      <section className="bg-admin-surface border border-line rounded-xl p-6 text-sm text-ink-soft leading-relaxed" data-testid="staff-handbook">
        <div className="flex items-center gap-2 mb-2"><BookOpen className="h-3.5 w-3.5 text-terracotta" strokeWidth={1.5} /><div className="overline">Staff Handbook · Constitution of the Digital Institution</div></div>
        <p>Every Digital Staff member follows one schema — <strong>Mission, Authority, Inputs, Outputs, Tools, Reports To, Approval Required, KPIs, Memory, Schedule, Limitations, Escalation Rules</strong>. Records begin as <em>defined</em> and only move to <em>active</em> once a human steward has reviewed the handbook entry and approved autonomous or semi-autonomous operation. No AI role executes outside its recorded authority.</p>
      </section>

      {selected && (
        <StaffDetail
          staff={selected}
          onClose={() => setSelected(null)}
          onSaved={(updated) => { setSelected(updated); load(); }}
          onArchived={() => { setSelected(null); load(); }}
        />
      )}

      {creating && (
        <NewStaffDialog
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function Tile({ label, value, icon: Icon, tone }) {
  return (
    <div className="bg-admin-surface border border-line rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        <Icon className={`h-3.5 w-3.5 ${tone === "terracotta" ? "text-terracotta" : "text-ink-soft"}`} strokeWidth={1.5} />
      </div>
      <div className="font-serif text-3xl mt-2">{value}</div>
    </div>
  );
}

function StaffDetail({ staff, onClose, onSaved, onArchived }) {
  const [form, setForm] = useState(staff);
  const [saving, setSaving] = useState(false);
  const [activity, setActivity] = useState([]);
  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(staff), [form, staff]);

  // Reset the form whenever a different staff record is opened, or when the
  // parent replaces the record (e.g. after save returns fresh server data).
  useEffect(() => { setForm(staff); }, [staff]);

  useEffect(() => {
    api.get(`/staff/${staff.id}/activity`).then(r => setActivity(r.data)).catch(() => {});
  }, [staff.id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/staff/${staff.id}`, form);
      toast.success("Handbook entry updated.");
      setForm(data);              // sync local state with server response (includes updated_at)
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const archive = async () => {
    if (!window.confirm(`Retire ${staff.name}? The role will remain visible in the Directory with a retired badge — this preserves institutional history.`)) return;
    try {
      await api.delete(`/staff/${staff.id}`);
      toast.success("Role retired and preserved in institutional history.");
      onArchived();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setLines = (k, v) => set(k, v.split("\n").map(s => s.trim()).filter(Boolean));

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-7xl max-h-[95vh] overflow-y-auto bg-admin-surface border-line" data-testid="staff-detail">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-ink flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-forest text-bone flex items-center justify-center font-serif text-xl">
              {form.name?.slice(0, 1)?.toUpperCase() || "·"}
            </div>
            {form.name}
          </DialogTitle>
          <div className="text-xs text-ink-soft flex items-center gap-2 flex-wrap">
            <Badge className={`text-[10px] uppercase tracking-widest border ${STATUS_TONE[form.status] || STATUS_TONE.defined}`}>{form.status}</Badge>
            <Badge className="bg-bone/60 text-ink border-line text-[10px]"><ShieldCheck className="h-3 w-3 mr-1" strokeWidth={1.5} />{AUTHORITY_LABEL[form.authority_level] || form.authority_level}</Badge>
            <span>{form.position} · {(form.department || "").replace(/_/g, " ")}</span>
          </div>
        </DialogHeader>

        <Tabs defaultValue="workspace" className="mt-4">
          <TabsList className="bg-bone/40 border border-line">
            <TabsTrigger value="workspace" data-testid="tab-workspace">Workspace</TabsTrigger>
            <TabsTrigger value="memory-score" data-testid="tab-memory-score">Memory Score</TabsTrigger>
            <TabsTrigger value="handbook" data-testid="tab-handbook">Handbook</TabsTrigger>
            <TabsTrigger value="operating" data-testid="tab-operating">Operating</TabsTrigger>
            <TabsTrigger value="cadence" data-testid="tab-cadence">Cadence</TabsTrigger>
            <TabsTrigger value="memory" data-testid="tab-memory">Memory &amp; Tools</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">Activity Log</TabsTrigger>
          </TabsList>

          {/* --- WORKSPACE — canonical AI Staff office --- */}
          <TabsContent value="workspace" className="space-y-4 mt-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs text-ink-soft">
                This is the {form.position}&rsquo;s permanent office. Every future AI Staff inherits this same architecture.
              </div>
              <Link
                to={`/admin/staff/${staff.id}/workspace`}
                data-testid="tab-workspace-open-full"
                className="text-xs text-terracotta hover:underline flex items-center gap-1"
              >
                Open full-page workspace <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
              </Link>
            </div>
            <StaffWorkspace staffId={staff.id} staffName={form.position} compact={true} />
          </TabsContent>

          {/* --- MEMORY SCORE — the maturation surface --- */}
          <TabsContent value="memory-score" className="space-y-4 mt-6">
            <MemoryScore staffId={staff.id} />
          </TabsContent>

          {/* --- HANDBOOK --- */}
          <TabsContent value="handbook" className="space-y-4 mt-6">
            <div className="grid md:grid-cols-2 gap-4">
              <F label="Name"><Input value={form.name || ""} onChange={e=>set("name", e.target.value)} data-testid="f-name" /></F>
              <F label="Position"><Input value={form.position || ""} onChange={e=>set("position", e.target.value)} data-testid="f-position" /></F>
              <F label="Department">
                <select value={form.department || ""} onChange={e=>set("department", e.target.value)} data-testid="f-department"
                  className="w-full h-10 rounded-md border border-line bg-bone px-3 text-sm">
                  {["executive","development","funding","programs","community","media","knowledge","archive","observatory","operations","economic_commons","technology","living_curriculum"].map(d =>
                    <option key={d} value={d}>{d.replace(/_/g, " ")}</option>
                  )}
                </select>
              </F>
              <F label="Status">
                <select value={form.status || "defined"} onChange={e=>set("status", e.target.value)} data-testid="f-status"
                  className="w-full h-10 rounded-md border border-line bg-bone px-3 text-sm">
                  {STAFF_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </F>
              <F label="Authority level">
                <select value={form.authority_level || "draft_only"} onChange={e=>set("authority_level", e.target.value)} data-testid="f-authority"
                  className="w-full h-10 rounded-md border border-line bg-bone px-3 text-sm">
                  {AUTHORITY_LEVELS.map(a => <option key={a} value={a}>{AUTHORITY_LABEL[a]}</option>)}
                </select>
              </F>
              <F label="Reports to"><Input value={form.reports_to || ""} onChange={e=>set("reports_to", e.target.value)} data-testid="f-reports-to" /></F>
              <F label="Human supervisor"><Input value={form.human_supervisor || ""} onChange={e=>set("human_supervisor", e.target.value)} /></F>
            </div>
            <F label="Mission"><Textarea rows={2} value={form.mission || ""} onChange={e=>set("mission", e.target.value)} data-testid="f-mission" /></F>
            <F label="Purpose"><Textarea rows={3} value={form.purpose || ""} onChange={e=>set("purpose", e.target.value)} /></F>
            <F label="Responsibilities (one per line)"><Textarea rows={6} value={(form.responsibilities || []).join("\n")} onChange={e=>setLines("responsibilities", e.target.value)} data-testid="f-responsibilities" /></F>
            <F label="Approval requirements (one per line)"><Textarea rows={4} value={(form.approval_requirements || []).join("\n")} onChange={e=>setLines("approval_requirements", e.target.value)} /></F>
            <F label="Limitations (one per line)"><Textarea rows={4} value={(form.limitations || []).join("\n")} onChange={e=>setLines("limitations", e.target.value)} /></F>
            <F label="Escalation rules (one per line)"><Textarea rows={4} value={(form.escalation_rules || []).join("\n")} onChange={e=>setLines("escalation_rules", e.target.value)} /></F>
            <F label="Notes"><Textarea rows={3} value={form.notes || ""} onChange={e=>set("notes", e.target.value)} /></F>
          </TabsContent>

          {/* --- OPERATING --- */}
          <TabsContent value="operating" className="space-y-4 mt-6">
            <F label="Inputs (one per line)"><Textarea rows={5} value={(form.inputs || []).join("\n")} onChange={e=>setLines("inputs", e.target.value)} /></F>
            <F label="Outputs (one per line)"><Textarea rows={5} value={(form.outputs || []).join("\n")} onChange={e=>setLines("outputs", e.target.value)} /></F>
            <F label="Standard operating procedures (one per line)"><Textarea rows={6} value={(form.standard_operating_procedures || []).join("\n")} onChange={e=>setLines("standard_operating_procedures", e.target.value)} /></F>
            <F label="KPIs (one per line)"><Textarea rows={5} value={(form.kpis || []).join("\n")} onChange={e=>setLines("kpis", e.target.value)} /></F>
          </TabsContent>

          {/* --- CADENCE --- */}
          <TabsContent value="cadence" className="space-y-4 mt-6">
            <CadenceEditor label="Daily tasks" items={form.daily_tasks || []} onChange={v=>set("daily_tasks", v)} cadence="daily" />
            <CadenceEditor label="Weekly tasks" items={form.weekly_tasks || []} onChange={v=>set("weekly_tasks", v)} cadence="weekly" />
            <CadenceEditor label="Monthly tasks" items={form.monthly_tasks || []} onChange={v=>set("monthly_tasks", v)} cadence="monthly" />
          </TabsContent>

          {/* --- MEMORY & TOOLS --- */}
          <TabsContent value="memory" className="space-y-4 mt-6">
            <F label="Memory scope"><Textarea rows={4} value={form.memory_scope || ""} onChange={e=>set("memory_scope", e.target.value)} /></F>
            <F label="Permissions (one per line)"><Textarea rows={4} value={(form.permissions || []).join("\n")} onChange={e=>setLines("permissions", e.target.value)} /></F>
            <F label="Internal tools (one per line)"><Textarea rows={4} value={(form.tools || []).join("\n")} onChange={e=>setLines("tools", e.target.value)} /></F>
            <F label="External integrations (one per line)"><Textarea rows={4} value={(form.external_integrations || []).join("\n")} onChange={e=>setLines("external_integrations", e.target.value)} /></F>
          </TabsContent>

          {/* --- ACTIVITY --- */}
          <TabsContent value="activity" className="space-y-3 mt-6">
            <div className="flex items-center gap-2 text-sm text-ink-soft">
              <Brain className="h-3.5 w-3.5 text-terracotta" strokeWidth={1.5} />
              Framework surface. Activity is populated later when this role begins executing (draft creation, approval requests, notes, handoffs).
            </div>
            {activity.length === 0 ? (
              <div className="rounded-md border border-dashed border-line p-6 text-center text-sm text-ink-soft">
                No activity recorded yet.
              </div>
            ) : (
              <ul className="divide-y divide-line rounded-md border border-line">
                {activity.map(a => (
                  <li key={a.id} className="px-4 py-3">
                    <div className="text-sm text-ink flex items-center gap-2">
                      <Badge className="bg-bone/60 text-ink border-line text-[10px]">{a.kind}</Badge>
                      {a.summary}
                    </div>
                    {a.detail && <div className="text-xs text-ink-soft mt-1">{a.detail}</div>}
                    <div className="text-[10px] text-ink-soft uppercase tracking-wider mt-1">
                      {new Date(a.created_at).toLocaleString()} · {a.initiated_by || "system"}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
        </Tabs>

        <div className="flex items-center justify-between pt-4 mt-2 border-t border-line">
          <button onClick={archive} className="text-xs text-ink-soft hover:text-terracotta" data-testid="staff-archive">
            Retire this role (soft delete)
          </button>
          <div className="flex items-center gap-2">
            <Button onClick={onClose} variant="outline" className="border-line">Close</Button>
            <Button onClick={save} disabled={!dirty || saving} data-testid="staff-save" className="bg-forest text-bone hover:bg-ink rounded-full">
              <Save className="h-4 w-4 mr-2" strokeWidth={1.5} /> {saving ? "Saving…" : "Save handbook"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CadenceEditor({ label, items, onChange, cadence }) {
  const add = () => onChange([...items, { label: "", cadence, description: "" }]);
  const update = (i, patch) => onChange(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="overline">{label}</Label>
        <button type="button" onClick={add} className="text-xs text-terracotta hover:underline">+ Add task</button>
      </div>
      {items.length === 0 && <div className="text-xs text-ink-soft">No tasks defined.</div>}
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2 items-start">
            <Input placeholder="Task label" value={it.label || ""} onChange={e=>update(i, { label: e.target.value })} />
            <Input placeholder="Description (optional)" value={it.description || ""} onChange={e=>update(i, { description: e.target.value })} />
            <button type="button" onClick={()=>remove(i)} className="text-ink-soft hover:text-terracotta shrink-0 mt-2"><X className="h-4 w-4" strokeWidth={1.5} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function NewStaffDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", position: "", department: "executive",
    mission: "", authority_level: "draft_only", status: "defined",
    responsibilities: [], approval_requirements: [], limitations: [],
    kpis: [], inputs: [], outputs: [], tools: [],
  });
  const [saving, setSaving] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.position || !form.mission) {
      toast.error("Name, position and mission are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/staff", form);
      toast.success(`${form.name} added to Staff Directory.`);
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-admin-surface border-line" data-testid="staff-new-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Define a new Digital Staff role</DialogTitle>
        </DialogHeader>
        <form onSubmit={create} className="space-y-4 mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Name"><Input required value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid="new-name" /></F>
            <F label="Position"><Input required value={form.position} onChange={e=>setForm({...form, position: e.target.value})} data-testid="new-position" /></F>
            <F label="Department">
              <select value={form.department} onChange={e=>setForm({...form, department: e.target.value})} data-testid="new-department"
                className="w-full h-10 rounded-md border border-line bg-bone px-3 text-sm">
                {["executive","development","funding","programs","community","media","knowledge","archive","observatory","operations","economic_commons","technology","living_curriculum"].map(d =>
                  <option key={d} value={d}>{d.replace(/_/g, " ")}</option>
                )}
              </select>
            </F>
            <F label="Authority level">
              <select value={form.authority_level} onChange={e=>setForm({...form, authority_level: e.target.value})}
                className="w-full h-10 rounded-md border border-line bg-bone px-3 text-sm">
                {AUTHORITY_LEVELS.map(a => <option key={a} value={a}>{AUTHORITY_LABEL[a]}</option>)}
              </select>
            </F>
          </div>
          <F label="Mission"><Textarea required rows={3} value={form.mission} onChange={e=>setForm({...form, mission: e.target.value})} data-testid="new-mission" /></F>
          <p className="text-xs text-ink-soft">
            All other fields (responsibilities, KPIs, cadence, memory, escalation rules) can be filled in after creation from the handbook view.
          </p>
          <div className="flex justify-end gap-2 pt-2 border-t border-line">
            <Button type="button" onClick={onClose} variant="outline" className="border-line">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="new-submit" className="bg-forest text-bone hover:bg-ink rounded-full">{saving ? "Creating…" : "Create Role"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return <div><Label className="overline mb-1.5 block">{label}</Label>{children}</div>;
}

// ============================================================================
// WORK AREA · Sprint 2 · Senior Grant Writer operational surface
// Shows assignments, generated outputs, and the approval queue for this role.
// Every AI-generated block is clearly banner-labeled and never externally sent.
// ============================================================================

function WorkArea({ staffId, staffPosition }) {
  const [assignments, setAssignments] = useState([]);
  const [outputs, setOutputs] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, o] = await Promise.all([
        api.get(`/staff/${staffId}/assignments`),
        api.get(`/staff/${staffId}/outputs`),
      ]);
      setAssignments(a.data);
      setOutputs(o.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  const pending = outputs.filter(o => o.approval_status === "pending");
  const approved = outputs.filter(o => o.approval_status === "approved");
  const rejected = outputs.filter(o => o.approval_status === "rejected");

  const decide = async (id, action, notes) => {
    try {
      if (action === "approve") await api.post(`/staff/outputs/${id}/approve`);
      else await api.post(`/staff/outputs/${id}/reject`, { notes });
      toast.success(action === "approve" ? "Approved. Draft cleared for founder use." : "Rejected. Feedback recorded.");
      setExpandedId(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="text-sm text-ink-soft">Loading work area…</div>;

  return (
    <div className="space-y-6" data-testid="work-area">
      {/* Summary strip */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniTile label="Assignments" value={assignments.length} icon={ClipboardList} />
        <MiniTile label="Pending" value={pending.length} icon={AlertTriangle} tone={pending.length > 0 ? "terracotta" : ""} />
        <MiniTile label="Approved" value={approved.length} icon={CheckCircle2} tone="forest" />
        <MiniTile label="Rejected" value={rejected.length} icon={XCircle} />
      </section>

      {/* Instruction — how to assign work */}
      {assignments.length === 0 && (
        <div className="rounded-xl border border-dashed border-line bg-bone/40 p-6" data-testid="work-empty-state">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-terracotta flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <div className="font-serif text-lg text-ink">No assignments yet</div>
              <p className="text-sm text-ink-soft mt-1 leading-relaxed">
                Open the <strong>Grant Center</strong> (/admin/grants). On any grant row, click <strong>&ldquo;Assign to {staffPosition}&rdquo;</strong>. The analysis takes ~30 seconds and returns fit score, draft narrative, required documents, and risks — waiting here for your approval.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pending approvals — most important */}
      {pending.length > 0 && (
        <section data-testid="work-pending">
          <h4 className="overline mb-3 text-terracotta">Pending Human Approval · {pending.length}</h4>
          <div className="space-y-3">
            {pending.map(o => (
              <OutputCard key={o.id} output={o} expanded={expandedId === o.id}
                onToggle={() => setExpandedId(expandedId === o.id ? null : o.id)}
                onDecide={decide} />
            ))}
          </div>
        </section>
      )}

      {approved.length > 0 && (
        <section data-testid="work-approved">
          <h4 className="overline mb-3">Approved · {approved.length}</h4>
          <div className="space-y-2">
            {approved.map(o => (
              <OutputCard key={o.id} output={o} compact expanded={expandedId === o.id}
                onToggle={() => setExpandedId(expandedId === o.id ? null : o.id)} />
            ))}
          </div>
        </section>
      )}

      {rejected.length > 0 && (
        <section data-testid="work-rejected">
          <h4 className="overline mb-3">Rejected · {rejected.length}</h4>
          <div className="space-y-2">
            {rejected.map(o => (
              <OutputCard key={o.id} output={o} compact expanded={expandedId === o.id}
                onToggle={() => setExpandedId(expandedId === o.id ? null : o.id)} />
            ))}
          </div>
        </section>
      )}

      {assignments.length > 0 && (
        <section data-testid="work-assignments-list">
          <h4 className="overline mb-3">All Assignments · {assignments.length}</h4>
          <ul className="divide-y divide-line rounded-md border border-line text-sm">
            {assignments.map(a => (
              <li key={a.id} className="px-4 py-2.5 flex items-center gap-3">
                <Badge className="bg-bone/60 text-ink border-line text-[10px]">{a.status}</Badge>
                <div className="flex-1 min-w-0 truncate">{a.target_name || a.target_id}</div>
                <div className="text-[10px] text-ink-soft uppercase tracking-wider">{new Date(a.created_at).toLocaleDateString()}</div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function MiniTile({ label, value, icon: Icon, tone }) {
  const toneClass = tone === "terracotta" ? "text-terracotta" : tone === "forest" ? "text-forest" : "text-ink";
  return (
    <div className="border border-line rounded-lg p-3 bg-bone/30">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
        <Icon className="h-3 w-3 text-ink-soft" strokeWidth={1.5} />
      </div>
      <div className={`font-serif text-2xl mt-1 ${toneClass}`}>{value}</div>
    </div>
  );
}

function OutputCard({ output, expanded, onToggle, onDecide, compact }) {
  const [rejectNote, setRejectNote] = useState("");
  const p = output.payload || {};
  const tu = output.token_usage || {};
  const statusColor = output.approval_status === "approved" ? "bg-forest text-bone" :
                      output.approval_status === "rejected" ? "bg-line text-ink-soft" :
                      "bg-terracotta text-bone";

  return (
    <div className="border border-line rounded-lg bg-admin-surface overflow-hidden" data-testid={`output-card-${output.id}`}>
      <button onClick={onToggle} className="w-full px-4 py-3 flex items-center gap-3 hover:bg-bone/40 transition-colors duration-150 text-left">
        <FileText className="h-4 w-4 text-terracotta flex-shrink-0" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-ink truncate">{output.target_name || "Grant analysis"}</div>
          <div className="text-[10px] text-ink-soft uppercase tracking-wider mt-0.5">
            {output.model_used?.split("/")?.pop()} · {new Date(output.created_at).toLocaleString()} · ~{tu.estimated_total || 0} tokens
          </div>
        </div>
        <Badge className={`text-[10px] uppercase tracking-widest ${statusColor}`}>{output.approval_status}</Badge>
        {p.fit_score !== undefined && (
          <div className="font-serif text-lg text-ink">{p.fit_score}<span className="text-xs text-ink-soft">/100</span></div>
        )}
        <ChevronRight className={`h-4 w-4 text-ink-soft transition-transform duration-150 ${expanded ? "rotate-90" : ""}`} strokeWidth={1.5} />
      </button>

      {expanded && (
        <div className="px-5 py-4 border-t border-line space-y-4 bg-bone/20" data-testid={`output-expanded-${output.id}`}>
          {/* AI-generated banner — always visible on every output */}
          <div className="rounded-md bg-terracotta/10 border border-terracotta/30 px-3 py-2 text-xs text-terracotta flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} />
            AI-generated draft — requires human review. Never externally submitted.
          </div>

          {p.fit_explanation && (
            <Section label="Mission fit"><p className="text-sm text-ink leading-relaxed">{p.fit_explanation}</p></Section>
          )}

          {p.opportunity_summary && (
            <Section label="Opportunity summary"><p className="text-sm text-ink leading-relaxed">{p.opportunity_summary}</p></Section>
          )}

          {p.draft_narrative && (
            <Section label="Draft narrative">
              <div className="text-sm text-ink leading-relaxed whitespace-pre-wrap bg-bone/50 border border-line rounded p-4 max-h-96 overflow-y-auto">{p.draft_narrative}</div>
            </Section>
          )}

          {p.required_documents_checklist?.length > 0 && (
            <Section label="Required documents">
              <ul className="text-sm text-ink space-y-1">
                {p.required_documents_checklist.map((d, i) => <li key={i} className="flex items-start gap-2"><span className="text-terracotta mt-1">·</span>{d}</li>)}
              </ul>
            </Section>
          )}

          {p.missing_information?.length > 0 && (
            <Section label="Missing information (gather before submission)">
              <ul className="text-sm text-ink space-y-1">
                {p.missing_information.map((d, i) => <li key={i} className="flex items-start gap-2"><span className="text-terracotta mt-1">·</span>{d}</li>)}
              </ul>
            </Section>
          )}

          {p.eligibility_notes?.length > 0 && (
            <Section label="Eligibility notes">
              <ul className="text-sm text-ink space-y-1">
                {p.eligibility_notes.map((d, i) => <li key={i} className="flex items-start gap-2"><span className="text-terracotta mt-1">·</span>{d}</li>)}
              </ul>
            </Section>
          )}

          {p.risks?.length > 0 && (
            <Section label="Risks &amp; concerns">
              <ul className="text-sm text-ink space-y-1">
                {p.risks.map((d, i) => <li key={i} className="flex items-start gap-2"><span className="text-terracotta mt-1">·</span>{d}</li>)}
              </ul>
            </Section>
          )}

          {p.recommended_next_step && (
            <Section label="Recommended next step"><p className="text-sm text-ink leading-relaxed italic">{p.recommended_next_step}</p></Section>
          )}

          {p.error && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">Parse error: {p.error}</div>
          )}

          {output.approval_status === "pending" && onDecide && (
            <div className="pt-3 border-t border-line space-y-3">
              <div className="text-xs text-ink-soft">
                <strong>Your review</strong> — approving marks the draft as founder-cleared for downstream use. Rejecting records feedback for future runs. Nothing is externally submitted either way.
              </div>
              <Textarea rows={2} placeholder="Optional feedback if rejecting…" value={rejectNote} onChange={e => setRejectNote(e.target.value)} data-testid={`reject-notes-${output.id}`} />
              <div className="flex gap-2">
                <Button onClick={() => onDecide(output.id, "approve")} data-testid={`approve-btn-${output.id}`} className="bg-forest text-bone hover:bg-ink rounded-full">
                  <CheckCircle2 className="h-4 w-4 mr-2" strokeWidth={1.5} /> Approve draft
                </Button>
                <Button onClick={() => onDecide(output.id, "reject", rejectNote)} variant="outline" className="border-line" data-testid={`reject-btn-${output.id}`}>
                  <XCircle className="h-4 w-4 mr-2" strokeWidth={1.5} /> Reject
                </Button>
              </div>
            </div>
          )}

          {output.approval_notes && (
            <div className="text-xs text-ink-soft italic border-t border-line pt-2">
              <strong>Feedback recorded:</strong> {output.approval_notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1.5">{label}</div>
      {children}
    </div>
  );
}

// ============================================================================
// MEMORY SCORE · How this role has matured through institutional service.
// Every number derived from real data. Confidence is a transparent formula.
// ============================================================================
function MemoryScore({ staffId }) {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get(`/staff/${staffId}/memory-score`)
      .then(r => { if (!cancelled) setScore(r.data); })
      .catch(() => { if (!cancelled) setScore(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [staffId]);

  if (loading) return <div className="text-sm text-ink-soft">Loading memory score…</div>;
  if (!score) return <div className="text-sm text-ink-soft">Memory score unavailable.</div>;

  const conf = score.confidence ?? 0;
  const notes = score.notes || {};

  return (
    <div className="space-y-6" data-testid="memory-score">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline flex items-center gap-2">
            <Brain className="h-3.5 w-3.5 text-terracotta" strokeWidth={1.5} />
            Experience &amp; Maturation
          </div>
          <p className="text-sm text-ink-soft mt-1 max-w-xl leading-relaxed">
            How this role has matured through institutional service. All values derived from real assignments and approvals — no invented numbers.
          </p>
        </div>
        <div className="text-[10px] text-ink-soft uppercase tracking-widest">
          Computed {new Date(score.computed_at).toLocaleString()}
        </div>
      </div>

      {/* Confidence — the composite headline signal */}
      <section className="bg-admin-surface border border-line rounded-xl p-6" data-testid="memory-confidence">
        <div className="flex items-baseline gap-4 flex-wrap">
          <div>
            <div className="overline">Confidence</div>
            <div className="font-serif text-6xl text-terracotta leading-none mt-2">{conf}<span className="text-2xl text-ink-soft">%</span></div>
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="h-2 bg-line rounded-full overflow-hidden">
              <div
                className="h-full bg-terracotta transition-all duration-700"
                style={{ width: `${Math.min(100, conf)}%` }}
              />
            </div>
            <p className="text-xs text-ink-soft mt-2 leading-relaxed">
              {conf === 0
                ? "No decisions yet. Confidence will rise as you approve or reject drafts."
                : conf < 50
                ? "Emerging trust — a handful of decisions are on record. Volume will strengthen this."
                : conf < 80
                ? "Growing confidence — the role's judgment has been validated multiple times."
                : "Mature confidence — the role has been trusted consistently across many decisions."}
            </p>
          </div>
        </div>
        <div className="text-[10px] text-ink-soft/70 uppercase tracking-widest mt-4 pt-3 border-t border-line">
          Formula · {notes.confidence_formula || "approval_rate × 0.7 + volume_boost × 0.3"}
        </div>
      </section>

      {/* Eight core metrics — the founder's mockup */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="memory-tiles">
        <MetricTile icon={ClipboardList} label="Assignments Completed" value={score.assignments_completed} />
        <MetricTile icon={CheckCircle2} label="Approved" value={score.approved} tone="forest" />
        <MetricTile icon={XCircle} label="Rejected" value={score.rejected} muted />
        <MetricTile icon={Award} label="Approval Rate" value={`${score.approval_rate}%`} tone="terracotta" />
        <MetricTile icon={HeartHandshake} label="Feedback Recorded" value={score.feedback_entries_recorded}
                    sub={score.feedback_char_total ? `${score.feedback_char_total} chars` : null} />
        <MetricTile icon={BookOpen} label="Institutional Memory Refs" value={score.institutional_memory_references} />
        <MetricTile icon={Clock} label="Estimated Time Saved" value={`${score.estimated_time_saved_hours}h`} tone="terracotta" />
        <MetricTile icon={TrendingUp} label="Confidence" value={`${conf}%`} tone="forest" />
      </section>

      {/* How this is computed — transparency footer */}
      <section className="rounded-xl border border-line bg-bone/40 p-5 text-xs text-ink-soft leading-relaxed space-y-2" data-testid="memory-footer">
        <div className="overline text-ink mb-1">How each number is computed</div>
        <ul className="space-y-1.5">
          <li><strong className="text-ink">Approval Rate</strong> — approved ÷ (approved + rejected). Undefined until you make your first decision.</li>
          <li><strong className="text-ink">Estimated Time Saved</strong> — {notes.estimated_time_saved}.</li>
          <li><strong className="text-ink">Feedback Recorded</strong> — {notes.feedback_meaning}</li>
          <li><strong className="text-ink">Institutional Memory References</strong> — {notes.memory_references_meaning}.</li>
          <li><strong className="text-ink">Confidence</strong> — {notes.confidence_formula}.</li>
        </ul>
        <p className="pt-2 border-t border-line text-ink-soft/80 italic">
          These numbers grow with real institutional service. No hardcoded percentages. When feedback learning is fully wired in a later sprint, this surface will show that too.
        </p>
      </section>
    </div>
  );
}

function MetricTile({ icon: Icon, label, value, sub, tone, muted }) {
  const numColor = tone === "forest" ? "text-forest" : tone === "terracotta" ? "text-terracotta" : muted ? "text-ink-soft" : "text-ink";
  return (
    <div className="border border-line rounded-lg p-4 bg-admin-surface" data-testid={`metric-${(label || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
        <Icon className="h-3 w-3 text-ink-soft" strokeWidth={1.5} />
      </div>
      <div className={`font-serif text-3xl mt-2 ${numColor}`}>{value}</div>
      {sub && <div className="text-[10px] text-ink-soft/70 mt-1">{sub}</div>}
    </div>
  );
}
